import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import s from './Pagination.module.scss';
import { useIntl } from 'react-intl';

const Pagination = ({
  data,
  pageSize = 20,
  startingPage = 1,
  children,
  onPageChange
}) => {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = useState(null);
  const [pageCount, setPageCount] = useState(null);

  const updateCurrentPage = (dataLength, pageSize, startingPage) => {
    let calculatedPageCount = parseInt(dataLength / pageSize);
    if (dataLength % pageSize > 0) {
      calculatedPageCount++;
    }
    setCurrentPage(startingPage);
    setPageCount(calculatedPageCount);
  };

  // Initial setup - equivalent to componentDidMount
  useEffect(() => {
    updateCurrentPage(data.length, pageSize, startingPage);
  }, []);

  // Handle data changes - equivalent to componentDidUpdate
  useEffect(() => {
    updateCurrentPage(data.length, pageSize, startingPage);
  }, [data.length, pageSize, startingPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const generateLink = (targetPage, disabled = false, text = null) => {
    if (!text && (targetPage <= 0 || targetPage > pageCount)) return null;
    
    return (
      <button
        key={text || targetPage}
        disabled={disabled}
        className={classNames({ [s.active]: targetPage === currentPage, [s.numbers]: !text })}
        onClick={() => handlePageChange(targetPage)}
      >
        {text || targetPage}
      </button>
    );
  };

  const paginationControls = () => {
    const intlNext = intl.formatMessage({ id: 'pagination.next', defaultMessage: '>' });
    const intlPrev = intl.formatMessage({ id: 'pagination.prev', defaultMessage: '<' });
    const intlshowing = intl.formatMessage({ id: 'pagination.showing', defaultMessage: 'Showing' });
    const intlof = intl.formatMessage({ id: 'pagination.of', defaultMessage: 'of' });

    let showingInfo = '';
    let showStartDataCount = 0;
    let showEndDataCount = 0;

    showStartDataCount = pageSize * (currentPage - 1) + 1;
    showEndDataCount = data.length > currentPage * pageSize ? currentPage * pageSize : data.length;

    if (data.length > 0 && showStartDataCount > 0 && showEndDataCount > 0) {
      showingInfo = `${intlshowing} ${showStartDataCount}-${showEndDataCount} ${intlof} ${data.length}`;
    }
    
    return (
      <Fragment>
        <div className={s.showing}>
          <span className={s.info}> {showingInfo}</span>
        </div>
        <div className={s.controls}>
          {generateLink(currentPage - 1, currentPage === 1, intlPrev)}
          <div className={s.pageLinks}>
            {generateLink(currentPage - 2)}
            {generateLink(currentPage - 1)}
            {generateLink(currentPage)}
            {generateLink(currentPage + 1)}
            {generateLink(currentPage + 2)}
          </div>
          {generateLink(currentPage + 1, currentPage === pageCount, intlNext)}
        </div>
      </Fragment>
    );
  };

  const paginatedData = () => {
    if (!currentPage) return [];
    
    const upperLimit = currentPage * pageSize;
    return data.slice(upperLimit - pageSize, upperLimit);
  };

  return (
    <div className={s.pagination}>
      {React.cloneElement(children, { currentPage: currentPage, paginatedData: paginatedData() })}
      {data.length > 0 && currentPage && paginationControls()}
    </div>
  );
};

Pagination.propTypes = {
  data: PropTypes.array.isRequired,
  pageSize: PropTypes.number,
  startingPage: PropTypes.number,
  children: PropTypes.element.isRequired,
  onPageChange: PropTypes.func
};

export default Pagination;